import { IPlanningSession } from "../models/models";
import { HierarchyNode, stratify } from "d3";
import { toIdMap } from "@gtmhub/util";
import dayjs from "@gtmhub/dayjs";

// the method respects the permissions between linked sessions, if a child has been aligned under a parent
// having access only to the child one will make it appear on a "root" level, otherwise the stratify fails
export const createSessionTree = (sessions: IPlanningSession[]): HierarchyNode<IPlanningSession> => {
  const fakeSessionRoot = { id: "root" };
  const sessionsMap = toIdMap(sessions);

  const buildTree = stratify<IPlanningSession>()
    .id((d) => d.id)
    .parentId((d) => {
      if (d.id === fakeSessionRoot.id) {
        return null;
      }

      return !d.parentId || !sessionsMap[d.parentId] ? fakeSessionRoot.id : d.parentId;
    });

  return buildTree([...sessions, fakeSessionRoot as IPlanningSession]);
};

export const calculateSessionsTimeframe = (
  sessions: IPlanningSession[],
  includeFullMonth = false
): { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs; timeframe: number } => {
  if (!sessions.length) {
    return {
      startDate: null,
      endDate: null,
      timeframe: 0,
    };
  }

  let startDate = dayjs(sessions[0].start);
  let endDate = dayjs(sessions[0].end);

  for (let i = 0; i < sessions.length; i++) {
    if (startDate.diff(sessions[i].start, "days") > 0) {
      startDate = dayjs(sessions[i].start);
    }

    if (endDate.diff(sessions[i].end, "days") < 1) {
      endDate = dayjs(sessions[i].end);
    }
  }

  if (includeFullMonth) {
    startDate = startDate.startOf("year");
    endDate = endDate.endOf("year");
  }

  return {
    startDate,
    endDate,
    timeframe: endDate.diff(startDate, "days"),
  };
};

export const generateTimePeriods = (startDateOfTimeframe: dayjs.Dayjs, endDateOfTimeframe: dayjs.Dayjs) => {
  const lines: Record<string, string[]> = {};
  for (let i = startDateOfTimeframe; i <= endDateOfTimeframe; i = i.add(1, "month")) {
    const year = i.format("YYYY");
    const month = i.format("MMM");
    if (!lines[year]) {
      lines[year] = [month];
    } else {
      lines[year].push(month);
    }
  }
  return lines;
};

export const generateLinesInSessionsGrid = (startDateOfTimeframe: dayjs.Dayjs, timeframe: number, linesCount: number): string[] => {
  const lines = [];
  // + 1 -> because the first one is at the start
  const timeframeLinesCount = linesCount + 1;
  // timeframe (305) / linesCount (0) = Infinity, so set 'range' to 0 instead of Infinity ;)
  const range = linesCount === 0 ? 0 : timeframe / linesCount;

  for (let i = 0; i < timeframeLinesCount; i++) {
    const l = range * i;
    lines.push(startDateOfTimeframe.add(l, "days").toISOString());
  }

  return lines;
};
